import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { TProfession } from '@onesource/schemas';
import { useGetProfessionsQuery } from '@onesource/services';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type TProps = Omit<TextFieldProps, 'name'> & {
  name: string;
  multiple?: boolean;
  maxSelections?: number;
};

export const RHFProfessionAutocomplete = ({
  name,
  disabled,
  label,
  helperText,
  multiple = false,
  maxSelections,
}: TProps) => {
  const { control } = useFormContext();
  const { data: professions = [], isLoading } = useGetProfessionsQuery();

  const options = useMemo(() => {
    const parents = new Map<number, string>();
    professions.filter(p => p.parentId === null).forEach(p => parents.set(p.id, p.name));

    return (
      professions
        .filter((p): p is TProfession & { parentId: number } => p.parentId !== null)
        .map(p => ({ id: String(p.id), label: p.name, group: parents.get(p.parentId) ?? '' }))
        // MUI Autocomplete groupBy renders a fresh group header every time the group
        // value changes while walking the list — a group whose options are not
        // contiguous shows its header twice. The API returns options ordered by id,
        // and ids are not grouped by parent, so sort by group here to keep each
        // group's options together.
        .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label))
    );
  }, [professions]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        if (multiple) {
          const currentIds: string[] = Array.isArray(field.value) ? field.value : [];
          const selectedOptions = options.filter(o => currentIds.includes(o.id));

          return (
            <Autocomplete
              multiple
              options={options}
              groupBy={opt => opt.group}
              getOptionLabel={opt => opt.label}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={selectedOptions}
              onChange={(_, vals) => {
                if (maxSelections !== undefined && vals.length > maxSelections) {
                  return;
                }
                field.onChange(vals.map(v => v.id));
              }}
              disabled={disabled || isLoading}
              loading={isLoading}
              renderTags={(vals, getTagProps) =>
                vals.map((opt, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} label={opt.label} size="small" {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  inputRef={field.ref}
                  label={label ?? 'Profession'}
                  error={!!error}
                  helperText={error?.message ?? helperText}
                  autoComplete="off"
                />
              )}
            />
          );
        }

        const currentId: string = typeof field.value === 'string' ? field.value : '';

        return (
          <Autocomplete
            options={options}
            groupBy={opt => opt.group}
            getOptionLabel={opt => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={options.find(o => o.id === currentId) ?? null}
            onChange={(_, val) => field.onChange(val ? val.id : '')}
            disabled={disabled || isLoading}
            loading={isLoading}
            renderInput={params => (
              <TextField
                {...params}
                inputRef={field.ref}
                label={label ?? 'Profession'}
                error={!!error}
                helperText={error?.message ?? helperText}
                autoComplete="off"
              />
            )}
          />
        );
      }}
    />
  );
};
